/**
 * "how you sign in" — the ways into this account, and, for an account that
 * was set up with Google and has no password, the control that adds one
 * (triage tri_mu32bbfv_109f93883d; founder ruling 2026-09-15: failure-time
 * hint on /auth PLUS in-app linking). After linking, Google and
 * email/password both open the same account — same uid, nothing migrates.
 *
 * Never imports firebase/auth (that import touches global fetch at load time
 * and takes whole suites down — the attachmentStorage lesson). The provider
 * list and the two Firebase calls arrive from Settings.tsx as props; the
 * decisions live in src/lib/identity/passwordLinking.ts.
 */
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import PasswordInput from '../common/PasswordInput';
import {
  describeSignInMethods,
  linkPasswordToSignedInUser,
  validateNewPassword,
  type LinkPasswordDeps,
  type LinkPasswordOutcome,
} from '../../lib/identity/passwordLinking';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Note = styled.p`
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.6;
  color: var(--brand-text-muted);
  max-width: 42rem;
`;

const Methods = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const MethodPill = styled.li`
  border: 1px solid #e8e4d9;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.82rem;
  background: #fcfbf7;
  text-transform: lowercase;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-width: 26rem;
`;

const Field = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 1px solid #e8e4d9;
  border-radius: 0.6rem;
  font-size: 0.95rem;
  background: #fff;
  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.15);
  }
`;

const SetButton = styled.button`
  align-self: flex-start;
  border: 1px solid var(--brand-gold);
  background: #fff;
  color: #7a5f1f;
  border-radius: 999px;
  padding: 0.45rem 1.1rem;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: lowercase;
  &:disabled { opacity: 0.6; cursor: wait; }
`;

const Problem = styled.div`
  font-size: 0.88rem;
  line-height: 1.6;
  color: #8a4b2a;
`;

const Done = styled.div`
  border: 1px solid rgba(27, 122, 61, 0.3);
  background: rgba(27, 122, 61, 0.05);
  color: #1b7a3d;
  border-radius: 0.6rem;
  padding: 0.6rem 0.8rem;
  font-size: 0.88rem;
  line-height: 1.5;
`;

export interface SignInMethodsCardProps {
  /** The signed-in account's email, for the copy. */
  email: string | null;
  /** User.providerData[].providerId — null while unknown. */
  providerIds: readonly string[] | null;
  /** The two Firebase calls, injected (see the module note). */
  linkDeps: LinkPasswordDeps;
  /** Called after a successful link so the parent re-reads the provider list. */
  onLinked?: () => void;
  /** Test seam. */
  linkPassword?: (password: string, deps: LinkPasswordDeps) => Promise<LinkPasswordOutcome>;
}

export const SignInMethodsCard: React.FC<SignInMethodsCardProps> = ({
  email,
  providerIds,
  linkDeps,
  onLinked,
  linkPassword = linkPasswordToSignedInUser,
}) => {
  const view = describeSignInMethods(providerIds);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [linked, setLinked] = useState(false);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validateNewPassword(password, confirm);
    if (invalid) { setProblem(invalid); return; }
    setBusy(true);
    setProblem(null);
    try {
      const outcome = await linkPassword(password, linkDeps);
      if (outcome.status === 'linked') {
        setLinked(true);
        setPassword('');
        setConfirm('');
        onLinked?.();
      } else {
        setProblem(outcome.message);
      }
    } finally {
      setBusy(false);
    }
  }, [password, confirm, linkPassword, linkDeps, onLinked]);

  if (providerIds === null) return null;

  return (
    <Wrap data-testid="sign-in-methods-card">
      <Methods aria-label="ways you can sign in">
        {view.hasGoogle && <MethodPill>google</MethodPill>}
        {(view.hasPassword || linked) && <MethodPill>email &amp; password</MethodPill>}
      </Methods>

      {linked ? (
        <Done role="status">
          done — from now on you can sign in with google or with {email || 'your email'} and the
          password you just set. both open this same account.
        </Done>
      ) : view.googleOnly ? (
        <>
          <Note>
            this account was set up with google, so it has no password. add one here and you'll be
            able to sign in either way — same account, same everything. we may ask you to confirm
            with google first.
          </Note>
          <Form onSubmit={submit} noValidate>
            <PasswordInput
              inputComponent={Field}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setProblem(null); }}
              placeholder="new password (at least 6 characters)"
              aria-label="new password"
              autoComplete="new-password"
              disabled={busy}
            />
            <PasswordInput
              inputComponent={Field}
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setProblem(null); }}
              placeholder="type it again"
              aria-label="confirm new password"
              autoComplete="new-password"
              disabled={busy}
            />
            {problem && <Problem role="alert">{problem}</Problem>}
            <SetButton type="submit" disabled={busy}>
              {busy ? 'setting your password…' : 'set a password'}
            </SetButton>
          </Form>
        </>
      ) : view.hasPassword ? (
        <Note>
          you can sign in with {email || 'your email'} and your password
          {view.hasGoogle ? ', or with google' : ''}. to change the password, use "forgot
          password?" on the sign-in page.
        </Note>
      ) : null}
    </Wrap>
  );
};

export default SignInMethodsCard;
